/* eslint-disable no-var */
import { ApplyHook, GetHook, SetHook } from 'partytown-wix-fork/integration'

// TODO: import these from Partytown fork
type WindowWithSandboxFunctions = Window & {
	_getRealNode: (node: Node) => Node | null
	_getFakeNode: (node: Node) => Node | null
	_addEventHandler: (node: Node, name: string, handler: EventListener | null) => void
	_addEventListener: (
		node: Node,
		type: string,
		callback: {
			originalListener: EventListenerOrEventListenerObject | null
			wrappedListener: EventListenerOrEventListenerObject | null
		},
		options?: AddEventListenerOptions | boolean
	) => void
	_removeEventListener: (node: Node, ...params: Parameters<EventTarget['removeEventListener']>) => void
	_triggerDefaultBehaviour: (node: Node, event: Event) => void
}

declare global {
	var windowGetRealNode: WindowWithSandboxFunctions['_getRealNode']
	var windowGetFakeNode: WindowWithSandboxFunctions['_getFakeNode']
	var windowAddEventHandler: WindowWithSandboxFunctions['_addEventHandler']
	var windowAddEventListener: WindowWithSandboxFunctions['_addEventListener']
	var resolvedFakeNodes: Map<Node, Node | null>
	var resolvedRealNodes: Map<Node, Node | null>
	var fnCallStack: Array<{ name: string; instance: any }>
}

export const getHook: GetHook = (opts) => {
	const { name, nodeName } = opts
	const optsWindow: WindowWithSandboxFunctions = opts.window as WindowWithSandboxFunctions

	if (
		!globalThis.windowGetRealNode &&
		!['_getRealNode', '_getFakeNode', '_addEventHandler', '_addEventListener'].includes(name)
	) {
		globalThis.windowGetRealNode = optsWindow._getRealNode
		globalThis.windowGetFakeNode = optsWindow._getFakeNode
		globalThis.windowAddEventHandler = optsWindow._addEventHandler
		globalThis.windowAddEventListener = optsWindow._addEventListener
	}

	if (nodeName && opts.instance) {
		const instance = (opts.instance as unknown) as Node
		const WHITELIST = [
			'activeElement',
			'clientWidth',
			'clientHeight',
			'clientTop',
			'clientLeft',
			'innerWidth',
			'innerHeight',
			'offsetWidth',
			'offsetHeight',
			'offsetTop',
			'offsetLeft',
			'outerWidth',
			'outerHeight',
			'pageXOffset',
			'pageYOffset',
			'scrollWidth',
			'scrollHeight',
			'scrollTop',
			'scrollLeft',
			'scrollTopMax',
			'scrollLeftMax',
			'sheet.cssRules',
			'value',
		]

		const nodeInDifferentDoc = WHITELIST.includes(name)
			? globalThis.windowGetRealNode?.(instance)
			: globalThis.windowGetFakeNode?.(instance)

		if (nodeInDifferentDoc) {
			const accessPath = name.split('.')
			const value = accessPath.reduce((acc: any, key) => acc[key], nodeInDifferentDoc)
			return value
		}
	}

	return opts.continue
}

export const setHook: SetHook = (opts) => {
	const { name, value, nodeName } = opts
	const optsWindow: WindowWithSandboxFunctions = opts.window as WindowWithSandboxFunctions

	if (!globalThis.windowGetRealNode) {
		globalThis.windowGetRealNode = optsWindow._getRealNode
		globalThis.windowGetFakeNode = optsWindow._getFakeNode
		globalThis.windowAddEventHandler = optsWindow._addEventHandler
		globalThis.windowAddEventListener = optsWindow._addEventListener
	}

	if (nodeName && opts.instance) {
		const instance = (opts.instance as unknown) as Node
		const isEventHandler = name.startsWith('on') && (typeof value === 'function' || !value)

		if (isEventHandler) {
			const wrappedHandler = value
				? (e: Event) => {
						const PREVENT_DEFAULT_SUPPORTED_EVENTS = ['click', 'submit']
						const retValue = value(e)

						if (e.cancelable && !e.defaultPrevented && PREVENT_DEFAULT_SUPPORTED_EVENTS.includes(e.type)) {
							optsWindow._triggerDefaultBehaviour(instance, e)
						}

						return retValue
				  }
				: null
			optsWindow._addEventHandler(instance, name, wrappedHandler)
			return opts.prevent
		}

		const WHITELIST: Array<string> = []
		const nodeInDifferentDoc = WHITELIST.includes(name)
			? globalThis.windowGetRealNode?.(instance)
			: globalThis.windowGetFakeNode?.(instance)

		if (nodeInDifferentDoc) {
			const accessPath = name.split('.')
			const lastAccessKey = accessPath[accessPath.length - 1]

			const target = accessPath.slice(0, -1).reduce((acc: any, key) => acc[key], nodeInDifferentDoc)
			target[lastAccessKey] = value
			return opts.prevent
		}
	}

	return opts.continue
}

export const applyHook: ApplyHook = (opts) => {
	globalThis.fnCallStack = globalThis.fnCallStack ?? []
	globalThis.resolvedFakeNodes = globalThis.resolvedFakeNodes ?? new Map()
	globalThis.resolvedRealNodes = globalThis.resolvedRealNodes ?? new Map()

	const FN_WHITELIST = [
		'removeEventListener',
		'getBoundingClientRect',
		'getClientRects',
		'scroll',
		'scrollBy',
		'scrollIntoView',
		'scrollIntoViewIfNeeded',
		'scrollTo',
		'focus',
		'blur',
		'play',
		'pause',
		'load',
		'sheet.addRule',
		'sheet.deleteRule',
		'sheet.insertRule',
		'sheet.removeRule',
		'sheet.replace',
		'sheet.replaceSync',
	]

	const FN_REAL_NODES_IN_ARGS_WHITELIST = ['observe', 'getComputedStyle']

	const { name, nodeName, args, instance } = opts
	const optsWindow: WindowWithSandboxFunctions = opts.window as WindowWithSandboxFunctions

	if (!globalThis.windowGetRealNode) {
		globalThis.windowGetRealNode = optsWindow._getRealNode
		globalThis.windowGetFakeNode = optsWindow._getFakeNode
		globalThis.windowAddEventHandler = optsWindow._addEventHandler
		globalThis.windowAddEventListener = optsWindow._addEventListener
	}

	const topCallStackEntry = globalThis.fnCallStack[globalThis.fnCallStack.length - 1]
	if (topCallStackEntry && topCallStackEntry.name === name && topCallStackEntry.instance === opts.instance) {
		return opts.continue
	}

	const invokeWithoutHook = <Result>(fn: () => Result): Result => {
		globalThis.fnCallStack.push({ name, instance })
		const result = fn()
		globalThis.fnCallStack.pop()
		return result
	}

	if (FN_REAL_NODES_IN_ARGS_WHITELIST.includes(name)) {
		const argsWithRealNodes = args.map((arg) =>
			arg instanceof (optsWindow.Node as typeof Node) ? globalThis.windowGetRealNode(arg) : arg
		)
		return invokeWithoutHook(() => {
			// @ts-expect-error
			return instance[name].apply(opts.instance, argsWithRealNodes)
		})
	} else if (name === '_getFakeNode') {
		const [firstArg] = args
		if (firstArg.nodeName === '#document' || firstArg.nodeName === 'REACT') {
			return null
		} else if (globalThis.resolvedFakeNodes.has(firstArg)) {
			return globalThis.resolvedFakeNodes.get(firstArg)
		} else {
			return invokeWithoutHook(() => {
				const resolvedNode = globalThis.windowGetFakeNode(firstArg)
				globalThis.resolvedFakeNodes.set(firstArg, resolvedNode)
				return resolvedNode
			})
		}
	} else if (name === '_getRealNode') {
		const [firstArg] = args
		if (firstArg.nodeName === '#document' || firstArg.nodeName === 'REACT') {
			return null
		} else if (globalThis.resolvedRealNodes.has(firstArg)) {
			return globalThis.resolvedRealNodes.get(firstArg)
		} else {
			return invokeWithoutHook(() => {
				const resolvedNode = globalThis.windowGetRealNode(firstArg)
				if (resolvedNode) {
					globalThis.resolvedRealNodes.set(firstArg, resolvedNode)
				}
				return resolvedNode
			})
		}
	}

	if (nodeName && opts.instance) {
		const instanceNode = (opts.instance as unknown) as Node

		if (name === 'addEventListener') {
			const [type, originalListener, ...restArgs] = args as Parameters<EventTarget['addEventListener']>
			const PREVENT_DEFAULT_SUPPORTED_EVENTS = ['click', 'submit']

			let wrappedListener: EventListener | null = null

			if (PREVENT_DEFAULT_SUPPORTED_EVENTS.includes(type)) {
				const eventCallback =
					typeof originalListener === 'function' ? originalListener : originalListener?.handleEvent

				wrappedListener = eventCallback
					? (e) => {
							eventCallback(e)

							if (e.cancelable && !e.defaultPrevented) {
								optsWindow._triggerDefaultBehaviour(instanceNode, e)
							}
					  }
					: null
			}

			return globalThis.windowAddEventListener(
				instanceNode,
				type,
				{
					originalListener,
					wrappedListener,
				},
				...restArgs
			)
		} else if (name === 'removeEventListener') {
			return optsWindow._removeEventListener(
				instanceNode,
				...(args as Parameters<EventTarget['removeEventListener']>)
			)
		}

		// This will return a node in the following cases:
		// 1. Function is whitelisted - we want to call it on the real node.
		// 2. The node belongs to the real document - we want to map it to the fake node. This can happen inside event listener callbacks, observer callbacks
		//    or any other cases when we somehow get a reference to the real node (usually we deal with fake nodes and not real ones).
		//    e.g. el.addEventListener('click', (e) => e.target.setHTML('exploit'));
		//    "instanceNode" variable will be the real node (from real doc) and "nodeInDifferentDoc" will be the fake node (from fake doc).
		//    We need to make sure user code cannot invoke functions on the real node, so we need to map it to the fake node.
		const nodeInDifferentDoc = FN_WHITELIST.includes(name)
			? globalThis.windowGetRealNode?.(instanceNode)
			: globalThis.windowGetFakeNode?.(instanceNode)

		if (nodeInDifferentDoc) {
			// If we decide to call a function on a different document node, we need to call same function with same arguments, just on a different node.
			// Let's say we execute such event listener callback: el.addEventListener('click', (e) => e.target.setHTML('exploit'));
			// "nodeInDifferentDoc" will be fake node (which is safe to work with)
			// "accessPath" will be ['setHTML']
			// "lastAccessKey" will be 'setHTML'
			// "target" will be the fake node
			// "fnToCall" will be reference to "setHTML" function
			const accessPath = name.split('.')
			const lastAccessKey = accessPath[accessPath.length - 1]

			const target = accessPath.slice(0, -1).reduce((acc: any, key) => acc[key], nodeInDifferentDoc)
			const fnToCall: Function = target[lastAccessKey]

			return fnToCall.bind(target)(...args)
		}
	}

	return opts.continue
}
